// LibraryService.js

import { httpsCallable } from 'firebase/functions';

import { functions } from 'firebaseConfig';
import { normalizeRollupDefinitions } from 'rollups/rollupDefinitions';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const DEFAULT_FALLBACK_VIEW_TYPE = 'LIST';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeDefaultView(value) {
  const normalized = String(value || '').trim().toUpperCase();
  return normalized || DEFAULT_FALLBACK_VIEW_TYPE;
}

function normalizeAddonView(raw) {
  return {
    description: String(raw?.description || '').trim(),
    title: String(raw?.title || '').trim() || 'Untitled View',
    type: String(raw?.type || DEFAULT_FALLBACK_VIEW_TYPE).trim().toUpperCase() || DEFAULT_FALLBACK_VIEW_TYPE,
    boardField: String(raw?.boardField || '').trim(),
    boardEnumOptions: raw?.boardEnumOptions && typeof raw.boardEnumOptions === 'object'
      ? raw.boardEnumOptions
      : null,
    timelineField: String(raw?.timelineField || '').trim(),
    timelineRowMode: String(raw?.timelineRowMode || '').trim() || 'flat',
    timelineGroupField: String(raw?.timelineGroupField || '').trim(),
    showUnscheduled: raw?.showUnscheduled == null ? true : Boolean(raw?.showUnscheduled),
    preset: String(raw?.preset || '').trim().toLowerCase() === 'gantt' ? 'gantt' : 'timeline',
    showHierarchy: raw?.showHierarchy != null
      ? Boolean(raw.showHierarchy)
      : String(raw?.timelineRowMode || '').trim().toLowerCase() === 'hierarchy',
    showDependencies: Boolean(raw?.showDependencies),
    allowScheduleEdit: Boolean(raw?.allowScheduleEdit),
    chartConfig: raw?.chartConfig && typeof raw.chartConfig === 'object'
      ? raw.chartConfig
      : null,
    properties: asArray(raw?.properties),
    filters: asArray(raw?.filters),
    sorts: asArray(raw?.sorts),
    includeDescendants: Boolean(raw?.includeDescendants),
    includeSubtitle: Boolean(raw?.includeSubtitle ?? raw?.includeDescription),
    includeCover: Boolean(raw?.includeCover),
  };
}

function normalizeAddonSchemaField(raw) {
  return {
    ...raw,
    includeByDefault: raw?.includeByDefault !== false,
  };
}

function normalizeAddonAutomationAction(raw) {
  return {
    ...raw,
    key: String(raw?.key || '').trim(),
    type: String(raw?.type || '').trim().toLowerCase() || 'set_field',
    fieldKey: String(raw?.fieldKey || '').trim(),
    target: raw?.target && typeof raw.target === 'object'
      ? {
        ...raw.target,
        kind: String(raw?.target?.kind || '').trim() || 'current_record',
        databaseKey: String(raw?.target?.databaseKey || '').trim(),
        nodeTypeKey: String(raw?.target?.nodeTypeKey || '').trim(),
      }
      : { kind: 'current_record', databaseKey: '', nodeTypeKey: '' },
    value: raw?.value && typeof raw.value === 'object'
      ? { ...raw.value }
      : { type: 'literal', literal: raw?.value ?? '' },
  };
}

function normalizeAddonAutomationRule(raw) {
  return {
    key: String(raw?.key || '').trim(),
    enabled: raw?.enabled !== false,
    title: String(raw?.title || '').trim(),
    description: String(raw?.description || '').trim(),
    scope: raw?.scope && typeof raw.scope === 'object'
      ? {
        ...raw.scope,
        databaseKey: String(raw?.scope?.databaseKey || '').trim(),
        nodeTypeKey: String(raw?.scope?.nodeTypeKey || '').trim(),
      }
      : { databaseKey: '', nodeTypeKey: '' },
    trigger: raw?.trigger && typeof raw.trigger === 'object'
      ? {
        ...raw.trigger,
        type: String(raw?.trigger?.type || '').trim() || 'field_changed',
        fieldKey: String(raw?.trigger?.fieldKey || '').trim(),
      }
      : { type: 'field_changed', fieldKey: '' },
    conditionGroups: asArray(raw?.conditionGroups).map((group) => ({
      ...group,
      key: String(group?.key || '').trim(),
      conditions: asArray(group?.conditions).map((condition) => ({
        ...condition,
        key: String(condition?.key || '').trim(),
        fieldKey: String(condition?.fieldKey || '').trim(),
        operator: String(condition?.operator || '').trim(),
        value: condition?.value && typeof condition.value === 'object'
          ? { ...condition.value }
          : { type: 'literal', literal: condition?.value ?? '' },
      })),
    })),
    conditions: asArray(raw?.conditions).map((condition) => ({
      ...condition,
      key: String(condition?.key || '').trim(),
      fieldKey: String(condition?.fieldKey || '').trim(),
      operator: String(condition?.operator || '').trim(),
      value: condition?.value && typeof condition.value === 'object'
        ? { ...condition.value }
        : { type: 'literal', literal: condition?.value ?? '' },
    })),
    actions: asArray(raw?.actions).map((action) => normalizeAddonAutomationAction(action)),
  };
}

export function normalizeLibraryTermKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

function normalizeAddonPayload(raw) {
  const schemaFields = asArray(raw?.schema?.fields).map((field) => normalizeAddonSchemaField(field));

  return {
    key: String(raw?.key || '').trim(),
    title: String(raw?.title || '').trim() || 'Addon',
    description: String(raw?.description || '').trim(),
    defaultView: normalizeDefaultView(raw?.defaultView),
    label: String(raw?.label || '').trim(),
    labelPlural: String(raw?.labelPlural || '').trim(),
    iconKey: String(raw?.iconKey || '').trim() || 'deployed_code',
    rollups: normalizeRollupDefinitions(raw?.rollups),
    views: asArray(raw?.views).map((view) => normalizeAddonView(view)),
    automations: {
      rules: asArray(raw?.automations?.rules).map((rule) => normalizeAddonAutomationRule(rule)),
    },
    schema: {
      fields: schemaFields,
    },
    selectionCount: Number(raw?.selectionCount || 0),
    dateCreated: raw?.dateCreated || null,
    dateModified: raw?.dateModified || null,
  };
}

function normalizeTermPayload(raw) {
  const addons = asArray(raw?.addons).map((addon) => normalizeAddonPayload(addon));
  const singular = String(raw?.label || '').trim() || String(raw?.key || '').trim();
  const fallbackPlural = String(addons[0]?.labelPlural || '').trim() || `${singular}s`;
  const labelPlural = String(raw?.labelPlural || raw?.pluralLabel || '').trim() || fallbackPlural;

  return {
    key: String(raw?.key || '').trim(),
    label: singular,
    labelLower: String(raw?.labelLower || '').trim() || normalizeLibraryTermKey(raw?.label || raw?.key),
    labelPlural,
    labelPluralLower: String(raw?.labelPluralLower || '').trim() || normalizeLibraryTermKey(labelPlural),
    searchTokens: asArray(raw?.searchTokens),
    addonsCount: Number(raw?.addonsCount || addons.length || 0),
    selectionCount: Number(raw?.selectionCount || 0),
    dateCreated: raw?.dateCreated || null,
    dateModified: raw?.dateModified || null,
    addons,
  };
}

export async function listLibraryEntries({ includeAddons = true } = {}) {
  const fn = httpsCallable(functions, 'listLibraryEntries');
  const { data } = await fn({ includeAddons: includeAddons !== false });
  const terms = asArray(data?.terms).map((term) => normalizeTermPayload(term));
  return terms;
}

export async function publishBaseTypeToLibrary({ baseKey, typeKey, title, description } = {}) {
  const fn = httpsCallable(functions, 'publishBaseTypeToLibrary');
  const { data } = await fn({ ...buildTenantCallableFields(baseKey), typeKey, title, description });
  return data || null;
}

export async function updateLibraryAddon({
  termKey,
  addonKey,
  title,
  description,
  defaultView,
  rollups,
  views,
  schemaFields,
  automations,
} = {}) {
  const fn = httpsCallable(functions, 'updateLibraryAddon');
  const { data } = await fn({
    termKey,
    addonKey,
    title,
    description,
    defaultView,
    rollups,
    views,
    schemaFields,
    automations,
  });
  return data || null;
}

export async function deleteLibraryAddon({ termKey, addonKey } = {}) {
  const fn = httpsCallable(functions, 'deleteLibraryAddon');
  const { data } = await fn({ termKey, addonKey });
  return data || null;
}

export async function provisionLibraryAddon({ baseKey, termKey, addonKey, selectedFieldKeys } = {}) {
  const fn = httpsCallable(functions, 'provisionLibraryAddon');
  const { data } = await fn({ ...buildTenantCallableFields(baseKey), termKey, addonKey, selectedFieldKeys });
  return data || null;
}
